import { API_BASE_URL } from "../../config";
import { createAuthenticatedClient } from "../../authenticatedClient";
import {
  CreateUserRequest,
  createUserRequestSchema,
  UserFilterDefinition,
  usersTableSuccessResponseSchema,
  UserTableRequestPayload,
  userFiltersResponseSchema,
  UsersTableSuccessResponse,
} from "../../schemas/userSchema/user.schema";
import { safeValidate } from "../../safeValidate";

const userAdminClient = createAuthenticatedClient(
  `${API_BASE_URL}/api/auth/admin/v1`,
);

/**
 * Enable when the backend /api/auth/admin/v1/users API supports pagination.
 * Set to `true` to send `pageSize` as a query parameter.
 */
const ENABLE_USER_LIST_PAGINATION = false;

function buildUsersQueryString(
  payload: UserTableRequestPayload,
  pageParam: string | null,
): string {
  const q = new URLSearchParams();
  if (payload.sortBy) {
    q.append("sortBy", payload.sortBy);
  }
  if (payload.sortOrder) {
    q.append("sortOrder", payload.sortOrder);
  }
  if (payload.search) {
    q.append("search", payload.search);
  }
  if (payload.status) {
    q.append("status", payload.status);
  }
  if (payload.role) {
    q.append("role", payload.role);
  }
  if (pageParam) {
    q.append("nextPageToken", pageParam);
  }
  if (ENABLE_USER_LIST_PAGINATION && payload.pageSize !== undefined) {
    q.append("pageSize", String(payload.pageSize));
  }
  return q.toString();
}

export const userApi = {
  getUsers: async (args: {
    pageParam: string | null;
    payload: UserTableRequestPayload;
  }): Promise<UsersTableSuccessResponse> => {
    const qs = buildUsersQueryString(args.payload, args.pageParam);
    const path = qs ? `/users?${qs}` : "/users";
    const data = await userAdminClient.get<unknown>(path);
    return safeValidate(
      usersTableSuccessResponseSchema,
      data,
      "userApi.getUsers",
    );
  },

  createUser: async (data: CreateUserRequest): Promise<unknown> => {
    const validated = createUserRequestSchema.parse(data);
    return userAdminClient.post<unknown>("/user/add", validated);
  },

  resetPassword: async (payload: { email: string }): Promise<unknown> => {
    return userAdminClient.put<unknown>("/password/reset", payload);
  },

  disableUser: async (payload: { email: string }): Promise<unknown> => {
    return userAdminClient.patch<unknown>("/user/disable", payload);
  },

  enableUser: async (payload: { email: string }): Promise<unknown> => {
    return userAdminClient.patch<unknown>("/user/enable", payload);
  },

  disableUserTOTP: async (payload: { email: string }): Promise<unknown> => {
    return userAdminClient.put<unknown>("/mfa/reset", payload);
  },

  getUserFilters: async (): Promise<UserFilterDefinition[]> => {
    const data = await userAdminClient.get<unknown>("/users/filters");
    return safeValidate(userFiltersResponseSchema, data, "getUserFilters");
  },
};
